import { useDebounce } from '@uidotdev/usehooks'
import { Button } from 'antd'
import classNames from 'classnames'
import { useContext, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'

import AtomDateRangePicker from '@/components/Atoms/AtomDateRangePicker/AtomDateRangePicker'
import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomVirtualizedTable from '@/components/Atoms/AtomVirtualizedTable/AtomVirtualizedTable'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import { DELAY } from '@/constants'
import OrderCard from '@/pages/Profile/Orders/OrderCard/OrderCard'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { useProfileStore } from '@/stores/user/profileStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'

import { useOrdersList } from './hooks/useOrdersList'
import {
  columns,
  isStatusFilter,
  ORDERS_COLUMN_WIDTHS,
  statusOptions,
} from './Orders.config'
import cn from './Orders.module.scss'
import type { OrderType } from './types/orders.types'

interface OrdersProps {
  orderType: OrderType
}

const LOAD_MORE_ROOT_MARGIN = '250px'
const ORDER_ROW_HEIGHT = 54

function Orders({ orderType }: OrdersProps) {
  const {
    searchQuery,
    setSearchQuery,
    productQuery,
    setProductQuery,
    selectedStatus,
    setSelectedStatus,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
  } = useProfileStore((state) => state)
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
  const [localProductQuery, setLocalProductQuery] = useState(productQuery)
  const debouncedQuery = useDebounce(localSearchQuery, DELAY)

  const selectedOrganisation = useUserInfoStore(
    (state) => state.selectedOrganisation
  )
  const userId = useUserInfoStore((state) => state.userId)
  const currentClientId = useUserInfoStore((state) => state.currentClient?.id)
  const navigate = useNavigate()
  const screen = useContext(ScreenSizeContext)
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const isMobileView = [screen?.md, screen?.sm, screen?.xs].some((v) => v)

  const { ordersQuery, orders, totalElements } = useOrdersList(
    {
      orderType,
      clientId: currentClientId,
      query: debouncedQuery,
      productQuery,
      status: selectedStatus,
      dateFrom,
      dateTo,
    },
    userId
  )
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = ordersQuery

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }

  const { ref: mobileLoadMoreRef } = useInView({
    rootMargin: LOAD_MORE_ROOT_MARGIN,
    onChange: (inView) => {
      if (inView) loadMore()
    },
  })

  const openOrder = (code: number) => navigate(`/profile/orders/${code}`)

  useEffect(() => {
    return () => {
      setSearchQuery('')
      setProductQuery('')
      setSelectedStatus('ALL')
      setDateFrom(null)
      setDateTo(null)
    }
  }, [
    setDateFrom,
    setDateTo,
    setProductQuery,
    setSearchQuery,
    setSelectedStatus,
  ])

  useEffect(() => {
    setSearchQuery(debouncedQuery)
  }, [debouncedQuery, setSearchQuery])

  useEffect(() => {
    setLocalProductQuery(productQuery)
  }, [productQuery])

  const renderOrders = () => {
    if (ordersQuery.isPending) return <Loader />
    if (ordersQuery.isError)
      return (
        <div className={cn.emptyMessage}>
          <span>
            {getGeneralErrorMessage(ordersQuery.error)}{' '}
            <Button type="link" onClick={() => ordersQuery.refetch()}>
              Повторить
            </Button>
          </span>
        </div>
      )
    if (orders.length === 0)
      return (
        <div className={cn.emptyMessage}>
          <span>Заказы не найдены</span>
        </div>
      )

    if (isMobileView)
      return (
        <div className={cn.orderCardWrapper}>
          <div className={cn.orderCards}>
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onClick={() => openOrder(order.code)}
              />
            ))}
          </div>
          {hasNextPage && (
            <div ref={mobileLoadMoreRef} className={cn.loadMoreTrigger}>
              {isFetchingNextPage && <Loader size={20} />}
            </div>
          )}
        </div>
      )

    return (
      <AtomVirtualizedTable
        ariaLabel="Заказы"
        data={orders}
        columns={columns}
        getRowId={(order) => order.id}
        columnWidths={ORDERS_COLUMN_WIDTHS}
        estimateRowHeight={ORDER_ROW_HEIGHT}
        isOrganisationSelected={isOrgSelected}
        onRowClick={(order) => openOrder(order.code)}
        hasFloatingHeader
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage,
          onLoadMore: loadMore,
        }}
      />
    )
  }

  return (
    <section className={cn.orders}>
      <div className={cn.filterBar}>
        <span
          className={classNames(
            cn.totalLabel,
            isOrgSelected && cn.totalLabelSelected
          )}
        >
          Заказов: {totalElements ?? '-'}
        </span>
        <div className={cn.statusSelect}>
          <HoverElement>
            <AtomSelect
              openByHover={false}
              outlined
              isOrganisationSelected={isOrgSelected}
              value={selectedStatus}
              handleChange={(status) => {
                if (typeof status === 'string' && isStatusFilter(status)) {
                  setSelectedStatus(status)
                }
              }}
              handleClear={() => setSelectedStatus('ALL')}
              options={statusOptions}
              allowClear
              popupClassName={
                isOrgSelected ? cn.statusPopupSelected : cn.statusPopup
              }
            />
          </HoverElement>
        </div>
        <AtomDateRangePicker
          className={cn.datePicker}
          value={{ from: dateFrom, to: dateTo }}
          onChange={({ from, to }) => {
            setDateFrom(from)
            setDateTo(from && to?.isBefore(from, 'day') ? null : to)
          }}
          isOrganisationSelected={isOrgSelected}
        />
        <AtomSearchInput
          placeholder="Артикул или товар..."
          className={cn.productSearchInput}
          value={localProductQuery}
          onChange={(e) => setLocalProductQuery(e.target.value)}
          onSearch={(val) => setProductQuery(val.trim())}
          isOrganisationSelected={isOrgSelected}
        />
        <AtomSearchInput
          placeholder="Код заказа или клиент..."
          className={cn.searchInput}
          value={localSearchQuery}
          onChange={(e) => setLocalSearchQuery(e.target.value)}
          onSearch={(val) => setLocalSearchQuery(val)}
          isOrganisationSelected={isOrgSelected}
        />
      </div>

      {renderOrders()}

      {!hasNextPage && orders.length > 0 && (
        <div className={cn.endOfList}>
          <span className={cn.endOfListText}>Все заказы загружены</span>
        </div>
      )}
    </section>
  )
}

export default Orders
